import { rem } from 'polished'
import type { PropsWithChildren, ReactNode } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'

import AccommodationRoomDetailsList from './AccommodationRoomDetailsList'
import { bedbankPlainDescription, getHotelRoomTypeName, mapRoomPackage } from './roomPackageUtils'

export const StyledAccommodationRoomTileDetails = styled(Group)`
  padding: ${rem(20)};
`

interface RoomPackageDetails {
  name: string
  description?: string
  urgencyLabelsPosition: 'above-description' | 'with-details-labels'
}

function extractPropertyRoomPackageDetails({ name, description }: App.Property.Room): RoomPackageDetails {
  return {
    name,
    description,
    urgencyLabelsPosition: 'with-details-labels',
  }
}

function extractHotelRoomPackageDetails(roomPackage: App.HotelPackage): RoomPackageDetails {
  return {
    name: getHotelRoomTypeName(roomPackage.roomType),
    description: roomPackage.roomType.description,
    urgencyLabelsPosition: 'above-description',
  }
}

function extractBedbankRoomPackageDetails(roomPackage: App.BedbankPackage): RoomPackageDetails {
  return {
    name: roomPackage.name,
    description: bedbankPlainDescription(roomPackage.description),
    urgencyLabelsPosition: 'above-description',
  }
}

interface Props {
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  urgencyLabels?: ReactNode
  actions?: ReactNode
  roomCount?: number
}

function AccommodationRoomTileDetails(props: PropsWithChildren<Props>) {
  const {
    actions,
    children,
    roomPackage,
    urgencyLabels,
    roomCount,
  } = props

  const intl = useIntl()

  const details = useMemo(() => {
    return mapRoomPackage(
      roomPackage,
      extractPropertyRoomPackageDetails,
      extractHotelRoomPackageDetails,
      extractBedbankRoomPackageDetails,
    )
  }, [roomPackage])

  const headingText = (roomCount && roomCount > 1)
    ? intl.formatMessage(
        {
          id: 'sKm8lePKALAPgd0iMYGT',
          defaultMessage: '{count}x {roomName}',
        },
        { count: roomCount, roomName: details.name },
      )
    : details.name

  return <StyledAccommodationRoomTileDetails direction="vertical" gap={12}>
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
        <Group direction="horizontal" gap={8}>
          <Heading variant="heading4">{headingText}</Heading>
        </Group>
        {actions && <Group direction="horizontal" gap={4} horizontalAlign="end" wrap="wrap" noDisplayWhenEmpty>
          {actions}
        </Group>}
      </Group>
      {details.urgencyLabelsPosition === 'above-description' && urgencyLabels && <LabelGroup>{urgencyLabels}</LabelGroup>}
    </VerticalSpacer>
    {details.description && <BodyText variant="medium">{details.description}</BodyText>}
    <AccommodationRoomDetailsList
      roomPackage={roomPackage}
      urgencyLabels={details.urgencyLabelsPosition === 'with-details-labels' ? urgencyLabels : undefined}
    />
    {children}
  </StyledAccommodationRoomTileDetails>
}

export default AccommodationRoomTileDetails
